import React from 'react';
import { SteeringConfig } from '../types';
import '../styles/SteeringPanel.css';

interface Props {
  steering: SteeringConfig;
  onChange: (updates: Partial<SteeringConfig>) => void;
}

const COGNITO_GROUPS = ['admin', 'ea-team', 'viewer', 'guest'];

const SteeringPanel: React.FC<Props> = ({ steering, onChange }) => {
  const handleGroupToggle = (group: string) => {
    const currentGroups = steering.allowedGroups;
    if (currentGroups.includes(group)) {
      onChange({
        allowedGroups: currentGroups.filter(g => g !== group)
      });
    } else {
      onChange({
        allowedGroups: [...currentGroups, group]
      });
    }
  };

  return (
    <div className="steering-panel">
      <h2>Steering Configuration</h2>
      <p className="subtitle">Configure how your app will be built and deployed</p>

      <div className="steering-sections">
        {/* Backend Configuration */}
        <section className="steering-section">
          <h3>Backend</h3>

          <div className="form-group">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={steering.hasBackend}
                onChange={e => onChange({
                  hasBackend: e.target.checked,
                  backendType: e.target.checked ? 'nodejs' : 'none',
                  dataStorage: e.target.checked ? 's3' : 'none'
                })}
              />
              <span>Enable Backend</span>
            </label>
            <small>Create a backend API for your app</small>
          </div>

          {steering.hasBackend && (
            <>
              <div className="form-group">
                <label>Backend Server</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="backendServer"
                      value="azure"
                      checked={steering.backendServer === 'azure' || !steering.backendServer}
                      onChange={() => onChange({ backendServer: 'azure' })}
                    />
                    <span className="radio-label">
                      <strong>Azure VM</strong> (20.222.180.9)
                      <small>Node.js + PM2, shared ea-backend</small>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="backendServer"
                      value="ec2"
                      checked={steering.backendServer === 'ec2'}
                      onChange={() => onChange({ backendServer: 'ec2' })}
                    />
                    <span className="radio-label">
                      <strong>AWS EC2</strong> (18.208.93.77)
                      <small>Python Lambda or standalone Node.js</small>
                    </span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Backend Type</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="backendType"
                      value="nodejs"
                      checked={steering.backendType === 'nodejs'}
                      onChange={() => onChange({ backendType: 'nodejs' })}
                    />
                    Node.js (Express)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="backendType"
                      value="python"
                      checked={steering.backendType === 'python'}
                      onChange={() => onChange({ backendType: 'python' })}
                    />
                    Python (Lambda)
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Data Storage</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="dataStorage"
                      value="s3"
                      checked={steering.dataStorage === 's3'}
                      onChange={() => onChange({ dataStorage: 's3', mysqlDbName: undefined })}
                    />
                    S3 (JSON files)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="dataStorage"
                      value="dynamodb"
                      checked={steering.dataStorage === 'dynamodb'}
                      onChange={() => onChange({ dataStorage: 'dynamodb', mysqlDbName: undefined })}
                    />
                    DynamoDB
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="dataStorage"
                      value="mysql"
                      checked={steering.dataStorage === 'mysql'}
                      onChange={() => onChange({ dataStorage: 'mysql' })}
                    />
                    MySQL (RDS database-2)
                  </label>
                </div>
              </div>

              {steering.dataStorage === 'mysql' && (
                <div className="form-group mysql-config">
                  <label>Database Name</label>
                  <input
                    type="text"
                    value={steering.mysqlDbName || ''}
                    onChange={e => onChange({ mysqlDbName: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    placeholder="my_app_db"
                  />
                  <small>A new database will be created on the RDS instance (database-2). Use lowercase letters, numbers, and underscores only.</small>
                </div>
              )}

              <div className="form-group">
                <label>API Prefix</label>
                <input
                  type="text"
                  value={steering.apiPrefix}
                  onChange={e => onChange({ apiPrefix: e.target.value })}
                  placeholder="/api"
                />
                <small>URL prefix for API endpoints (e.g., /api/data)</small>
              </div>
            </>
          )}
        </section>

        {/* Authentication Configuration */}
        <section className="steering-section">
          <h3>Authentication</h3>

          <div className="form-group">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={steering.authRequired}
                onChange={e => onChange({ authRequired: e.target.checked })}
              />
              <span>Require Authentication</span>
            </label>
            <small>Users must login to access this app</small>
          </div>

          {steering.authRequired && (
            <div className="form-group">
              <label>Allowed Groups</label>
              <small>Select which Cognito groups can access this app</small>
              <div className="group-checkboxes">
                {COGNITO_GROUPS.map(group => (
                  <label key={group} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={steering.allowedGroups.includes(group)}
                      onChange={() => handleGroupToggle(group)}
                    />
                    <span>{group}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Generation Method */}
        <section className="steering-section">
          <h3>Code Generation</h3>

          <div className="form-group">
            <label>Generation Method</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="generationMethod"
                  value="llm"
                  checked={steering.generationMethod === 'llm' || !steering.generationMethod}
                  onChange={() => onChange({ generationMethod: 'llm' })}
                />
                <span className="radio-label">
                  <strong>LLM-Powered</strong> (Recommended)
                  <small>AI generates functional React code based on your requirements</small>
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="generationMethod"
                  value="template"
                  checked={steering.generationMethod === 'template'}
                  onChange={() => onChange({ generationMethod: 'template' })}
                />
                <span className="radio-label">
                  <strong>Template-Based</strong>
                  <small>Uses predefined component templates</small>
                </span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Chat Assistant Access</label>
            <small>Enable in-app chat panel to enhance the deployed app with AI</small>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="chatEnabled"
                  value="all"
                  checked={steering.chatEnabled === 'all'}
                  onChange={() => onChange({ chatEnabled: 'all' })}
                />
                All Users
              </label>
              <label>
                <input
                  type="radio"
                  name="chatEnabled"
                  value="admin"
                  checked={steering.chatEnabled === 'admin'}
                  onChange={() => onChange({ chatEnabled: 'admin' })}
                />
                Admin Only
              </label>
              <label>
                <input
                  type="radio"
                  name="chatEnabled"
                  value="disabled"
                  checked={steering.chatEnabled === 'disabled' || !steering.chatEnabled}
                  onChange={() => onChange({ chatEnabled: 'disabled' })}
                />
                Disabled
              </label>
            </div>
            {(steering.chatEnabled === 'all' || steering.chatEnabled === 'admin') && (
              <div className="info-box">
                A floating chat button will be added to your deployed app. Users can request new features,
                bug fixes, or enhancements directly from the app. Changes are applied automatically.
              </div>
            )}
          </div>
        </section>

        {/* Claude Code SDK Capabilities */}
        {steering.generationMethod === 'llm' && (
          <section className="steering-section">
            <h3>Claude Code SDK</h3>
            <p className="subtitle">Enable AI-powered capabilities for your app via Claude Code SDK</p>

            <div className="form-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={steering.sdkEnabled || false}
                  onChange={e => onChange({
                    sdkEnabled: e.target.checked,
                    sdkChat: e.target.checked ? steering.sdkChat : false,
                    sdkCrosscheck: e.target.checked ? steering.sdkCrosscheck : false,
                    sdkDocgen: e.target.checked ? steering.sdkDocgen : false,
                    sdkLlmBridge: e.target.checked ? steering.sdkLlmBridge : false,
                  })}
                />
                <span>Enable SDK Capabilities</span>
              </label>
              <small>Scaffold AI features into the generated app</small>
            </div>

            {steering.sdkEnabled && (
              <>
                <div className="form-group">
                  <div className="group-checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={steering.sdkChat || false}
                        onChange={e => onChange({ sdkChat: e.target.checked })}
                      />
                      <span>AI Chat Panel</span>
                      <small style={{ display: 'block', marginLeft: '1.5rem', color: '#6c757d' }}>
                        Connect to Ollama LLM for in-app AI conversations
                      </small>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={steering.sdkCrosscheck || false}
                        onChange={e => onChange({ sdkCrosscheck: e.target.checked })}
                      />
                      <span>Cross-Check Validation</span>
                      <small style={{ display: 'block', marginLeft: '1.5rem', color: '#6c757d' }}>
                        AI-powered data validation and consistency checks
                      </small>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={steering.sdkDocgen || false}
                        onChange={e => onChange({ sdkDocgen: e.target.checked })}
                      />
                      <span>Document Generation</span>
                      <small style={{ display: 'block', marginLeft: '1.5rem', color: '#6c757d' }}>
                        Auto-generate reports and documents from app data
                      </small>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={steering.sdkLlmBridge || false}
                        onChange={e => onChange({ sdkLlmBridge: e.target.checked })}
                      />
                      <span>LLM Bridge</span>
                      <small style={{ display: 'block', marginLeft: '1.5rem', color: '#6c757d' }}>
                        Backend bridge to Ollama server for custom LLM queries
                      </small>
                    </label>
                  </div>
                </div>

                {(steering.sdkChat || steering.sdkLlmBridge) && (
                  <div className="form-group">
                    <label>LLM Model</label>
                    <select
                      value={steering.llmModel || 'qwen3:8b'}
                      onChange={e => onChange({ llmModel: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                    >
                      <option value="qwen3:8b">Qwen3 8B - Best all-round (Default)</option>
                      <option value="qwen2.5:7b">Qwen2.5 7B - Fast general purpose</option>
                      <option value="llama3.1:8b">Llama 3.1 8B - English, good at code</option>
                      <option value="gemma-2-2b-jpn-it">Gemma 2 2B JP - Japanese, lightweight</option>
                      <option value="qwen3:14b">Qwen3 14B - Higher quality</option>
                      <option value="llama3.1:70b">Llama 3.1 70B - Best quality (slow)</option>
                    </select>
                    <small>Ollama model on LLM Server (34.204.20.142:5050)</small>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Wizard Skip Options */}
        {steering.generationMethod === 'llm' && (
          <section className="steering-section">
            <h3>Wizard Steps</h3>
            <p className="subtitle">Choose which steps the provisioning wizard should execute</p>

            <div className="form-group">
              <div className="group-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!steering.skipCodeGen}
                    onChange={e => onChange({ skipCodeGen: !e.target.checked })}
                  />
                  <span>Generate App Code (Claude Code SDK)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!steering.skipDocs}
                    onChange={e => onChange({ skipDocs: !e.target.checked })}
                  />
                  <span>Generate Documentation (10 deliverables: MD, PPT, TCS PPT, PDF x EN/JP)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!steering.skipVideos}
                    onChange={e => onChange({ skipVideos: !e.target.checked })}
                  />
                  <span>Generate Demo Videos (Japanese + English, 60-120s each)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!steering.skipGithub}
                    onChange={e => onChange({ skipGithub: !e.target.checked })}
                  />
                  <span>Push to GitHub</span>
                </label>
              </div>
            </div>
          </section>
        )}

        {/* Summary */}
        <section className="steering-section summary">
          <h3>Configuration Summary</h3>
          <table className="summary-table">
            <tbody>
              <tr>
                <td>Generation Method</td>
                <td>{steering.generationMethod === 'template' ? 'Template-Based' : 'LLM-Powered'}</td>
              </tr>
              <tr>
                <td>Backend</td>
                <td>{steering.hasBackend ? `${steering.backendType} on ${steering.backendServer || 'azure'} + ${steering.dataStorage}` : 'No backend'}</td>
              </tr>
              {steering.dataStorage === 'mysql' && steering.mysqlDbName && (
                <tr>
                  <td>MySQL Database</td>
                  <td>{steering.mysqlDbName} (on database-2 RDS)</td>
                </tr>
              )}
              <tr>
                <td>Authentication</td>
                <td>{steering.authRequired ? 'Required' : 'Public'}</td>
              </tr>
              {steering.authRequired && (
                <tr>
                  <td>Allowed Groups</td>
                  <td>{steering.allowedGroups.join(', ') || 'None'}</td>
                </tr>
              )}
              {steering.hasBackend && (
                <tr>
                  <td>API Prefix</td>
                  <td>{steering.apiPrefix}</td>
                </tr>
              )}
              <tr>
                <td>Chat Assistant</td>
                <td>{steering.chatEnabled === 'all' ? 'All Users' : steering.chatEnabled === 'admin' ? 'Admin Only' : 'Disabled'}</td>
              </tr>
              {steering.sdkEnabled && (
                <tr>
                  <td>SDK Capabilities</td>
                  <td>
                    {[
                      steering.sdkChat && 'Chat',
                      steering.sdkCrosscheck && 'CrossCheck',
                      steering.sdkDocgen && 'DocGen',
                      steering.sdkLlmBridge && 'LLM Bridge',
                    ].filter(Boolean).join(', ') || 'None'}
                  </td>
                </tr>
              )}
              {(steering.sdkChat || steering.sdkLlmBridge) && steering.llmModel && (
                <tr>
                  <td>LLM Model</td>
                  <td>{steering.llmModel}</td>
                </tr>
              )}
              {steering.generationMethod === 'llm' && (
                <tr>
                  <td>Wizard Steps</td>
                  <td>
                    {[
                      !steering.skipCodeGen && 'Code Gen',
                      !steering.skipDocs && 'Docs',
                      !steering.skipVideos && 'Videos',
                      !steering.skipGithub && 'GitHub',
                    ].filter(Boolean).join(', ') || 'Infrastructure only'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default SteeringPanel;
